import { useCallback, useEffect, useMemo, useState } from 'react'
import { DEFAULT_SETTINGS_SETUP } from './constants'

const getInitialSetupTypeState = ({ initialSetupType, initialSetupCompany }) => {
  if (initialSetupType && !initialSetupCompany) {
    return initialSetupType
  }
  return initialSetupCompany ? DEFAULT_SETTINGS_SETUP.SELECT_COMPANY : DEFAULT_SETTINGS_SETUP.GLOBAL
}

const checkIsValid = ({ setupType, setupCompany }) =>
  (setupType === DEFAULT_SETTINGS_SETUP.SELECT_COMPANY && setupCompany) || setupType === DEFAULT_SETTINGS_SETUP.GLOBAL

/**
 * Handles setup controls and picks the initial form state based on the setup company
 * @param {Array} engageDefaultSettings array with all engage default settings
 * @param {String} action string with the action type (onboading, offboarding, ...)
 * @param {Number} initialSetupCompany id of the selected company when this hook is initialized
 * @returns
 */
const useDefaultSettingsSetup = ({ engageDefaultSettings, action, initialSetupCompany = null, initialSetupType }) => {
  const [setupType, setSetupType] = useState(getInitialSetupTypeState({ initialSetupType, initialSetupCompany }))
  const [setupCompany, setSetupCompany] = useState(initialSetupCompany)
  const [isValidSetup, setIsValidSetup] = useState(true)
  const [isSetupCompanyTouched, setIsSetupCompanyTouched] = useState(false) // control when to show error on the company selector

  const onSetupTypeChange = useCallback((type) => {
    setSetupType(type)
    setSetupCompany(null) // if setup type is changing clear company selections
    setIsSetupCompanyTouched(false) // reset touched when changing to global
  }, [])

  const onSetupCompanyChange = useCallback((company) => {
    setSetupCompany(company)
    setIsSetupCompanyTouched(true)
  }, [])

  // return settings based on the setup type and company selected
  const selectedSettings = useMemo(() => {
    if (!checkIsValid({ setupType, setupCompany })) {
      return null // avoid returning global settings if company was not selected yet
    }
    return engageDefaultSettings?.find((item) => item.action === action && item.customerId === setupCompany)
  }, [action, setupCompany, setupType, engageDefaultSettings])

  // validate setup selections whenever they change
  useEffect(() => {
    const isValid = checkIsValid({ setupType, setupCompany })
    setIsValidSetup(isValid)
  }, [setupCompany, setupType])

  // force a validation, return `isValidSetup`
  const validateSetup = useCallback(() => {
    setIsSetupCompanyTouched(true)
    return isValidSetup
  }, [isValidSetup])

  return {
    setupType,
    onSetupTypeChange,
    setupCompany,
    onSetupCompanyChange,
    initialSettings: selectedSettings?.settings,
    settingId: selectedSettings?.id,
    isSettingUpTenant: setupType === DEFAULT_SETTINGS_SETUP.SELECT_COMPANY,
    isValidSetup,
    isSetupCompanyTouched,
    setIsSetupCompanyTouched,
    validateSetup,
  }
}

export default useDefaultSettingsSetup
